import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';

import { useWalletContext } from '#src/contexts/wallet/index.ts';
import { isType } from '#src/lib/gql/index.ts';

import type { useGetAuthorizationApprovals_authorizationRequest } from './__generated__/useGetAuthorizationApprovals.graphql.ts';

export const useGetAuthorizationApprovals = () => {
  const { approveAuthorizationRequests } = useWalletContext();

  return useCallback(
    async (
      authorizations: useGetAuthorizationApprovals_authorizationRequest[]
    ) => {
      const walletAuthorizationRequests = authorizations
        .map(({ fingerprint, request }) =>
          isType(request, 'StripeCreditCardAuthorizationRequest') ||
          isType(request, 'MangopayCreditCardAuthorizationRequest') ||
          isType(request, 'CheckoutCreditCardAuthorizationRequest') ||
          isType(request, 'PaypalAuthorizationRequest') ||
          isType(request, 'MangopayKlarnaAuthorizationRequest') ||
          isType(request, 'MangopayApplePayAuthorizationRequest') ||
          isType(request, 'StarkexV6TokenTransferAuthorizationRequest')
            ? null
            : {
                ...request,
                fingerprint,
              }
        )
        .filter(Boolean)
        .map(request => {
          if (isType(request, 'MangopayWalletTransferAuthorizationRequest')) {
            return { ...request, amount: request.fiatAmount };
          }
          if (isType(request, 'SolanaTokenTransferAuthorizationRequest')) {
            return {
              ...request,
              senderAddress: request.solanaSenderAddress,
              receiverAddress: request.solanaReceiverAddress,
              nonce: request.solanaNonce,
            };
          }
          if (isType(request, 'SolanaBankTransferAuthorizationRequest')) {
            return {
              ...request,
              senderAddress: request.solanaSenderAddress,
              receiverAddress: request.solanaReceiverAddress,
              amount: request.cryptocurrencyAmount,
              feeAmount: request.cryptocurrencyFeeAmount,
              nonce: request.solanaNonce,
            };
          }
          if (
            isType(request, 'SolanaBankConditionalTransferAuthorizationRequest')
          ) {
            return {
              ...request,
              senderAddress: request.solanaSenderAddress,
              receiverAddress: request.solanaReceiverAddress,
              amount: request.cryptocurrencyAmount,
              feeAmount: request.cryptocurrencyFeeAmount,
              nonce: request.solanaNonce,
            };
          }
          if (isType(request, 'SolanaBankWithdrawalAuthorizationRequest')) {
            return {
              ...request,
              senderAddress: request.solanaSenderAddress,
              receiverAddress: request.solanaReceiverAddress,
              amount: request.cryptocurrencyAmount,
              feeAmount: request.cryptocurrencyFeeAmount,
              nonce: request.solanaNonce,
            };
          }
          return request;
        });
      return approveAuthorizationRequests(walletAuthorizationRequests);
    },
    [approveAuthorizationRequests]
  );
};

export const useGetAuthorizationApproval = () => {
  const getAuthorizationApprovals = useGetAuthorizationApprovals();

  return useCallback(
    async (
      authorization: useGetAuthorizationApprovals_authorizationRequest
    ) => {
      const { authorizationApprovals } = await getAuthorizationApprovals([
        authorization,
      ]);
      return authorizationApprovals?.[0];
    },
    [getAuthorizationApprovals]
  );
};

useGetAuthorizationApprovals.fragments = {
  authorizationRequest: gql`
    fragment useGetAuthorizationApprovals_authorizationRequest on AuthorizationRequest {
      id
      fingerprint
      request {
        ... on StarkexLimitOrderAuthorizationRequest {
          vaultIdSell
          vaultIdBuy
          amountSell
          amountBuy
          tokenSell
          tokenBuy
          nonce
          expirationTimestamp
          feeInfo {
            feeLimit
            sourceVaultId
            tokenId
          }
        }
        ... on StarkexTransferAuthorizationRequest {
          amount
          condition
          expirationTimestamp
          feeInfoUser {
            feeLimit
            sourceVaultId
            tokenId
          }
          nonce
          receiverPublicKey
          receiverVaultId
          senderVaultId
          token
        }
        ... on SolanaTokenTransferAuthorizationRequest {
          solanaSenderAddress: senderAddress
          solanaReceiverAddress: receiverAddress
          assetId
          solanaNonce: nonce
          expirationTimestamp
          leafIndex
          merkleTreeAddress
          transferProxyProgramAddress
          originator
        }
        ... on SolanaBankTransferAuthorizationRequest {
          solanaSenderAddress: senderAddress
          solanaReceiverAddress: receiverAddress
          cryptocurrencyAmount: amount
          cryptocurrencyFeeAmount: feeAmount
          solanaNonce: nonce
          expirationTimestamp
          originator
          bankProgramAddress
          authority
        }
        ... on SolanaBankConditionalTransferAuthorizationRequest {
          solanaSenderAddress: senderAddress
          solanaReceiverAddress: receiverAddress
          cryptocurrencyAmount: amount
          cryptocurrencyFeeAmount: feeAmount
          solanaNonce: nonce
          expirationTimestamp
          originator
          bankProgramAddress
          authority
          conditionalAmount
          conditionalCurrency
          conditionalNetwork
          conditionalReceiverAddress
          conditionalSalt
        }
        ... on SolanaBankWithdrawalAuthorizationRequest {
          solanaSenderAddress: senderAddress
          solanaReceiverAddress: receiverAddress
          cryptocurrencyAmount: amount
          cryptocurrencyFeeAmount: feeAmount
          solanaNonce: nonce
          expirationTimestamp
          originator
          bankProgramAddress
          authority
        }
        ... on EthereumBankTransferAuthorizationRequest {
          senderAddress
          receiverAddress
          amount
          feeAmount
          deadline
          salt
          proxyAddress
          contractAddress
        }
        ... on EthereumBankConditionalTransferAuthorizationRequest {
          senderAddress
          receiverAddress
          amount
          feeAmount
          deadline
          salt
          proxyAddress
          contractAddress
          conditionalAmount
          conditionalCurrency
          conditionalNetwork
          conditionalReceiverAddress
          conditionalSalt
        }
        ... on BaseWithdrawalAuthorizationRequest {
          senderAddress
          receiverAddress
          destinationAddress
          amount
          feeAmount
          deadline
          salt
          proxyAddress
          contractAddress
        }
        ... on MangopayWalletTransferAuthorizationRequest {
          nonce
          fiatAmount: amount
          currency
          operationHash
          mangopayWalletId
        }
        ... on StripeCreditCardAuthorizationRequest {
          id
          clientSecret
        }
        ... on MangopayCreditCardAuthorizationRequest {
          id
        }
        ... on PaypalAuthorizationRequest {
          id
          redirectUrl
        }
        ... on MangopayKlarnaAuthorizationRequest {
          id
          redirectUrl
        }
        ... on MangopayApplePayAuthorizationRequest {
          id
        }
        ... on CheckoutCreditCardAuthorizationRequest {
          id
        }
      }
    }
  ` as TypedDocumentNode<useGetAuthorizationApprovals_authorizationRequest>,
};

useGetAuthorizationApproval.fragments = useGetAuthorizationApprovals.fragments;
